import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Award,
  BookOpen,
  CheckCircle2,
  Target,
  TrendingUp,
  Users,
  FileCheck,
  BarChart3,
  GraduationCap,
  Shield,
  Lightbulb,
  Building2,
  ChevronRight,
  Star,
  Heart,
  Globe
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

export const ISO21001EducationalOrganizations = () => {
  const navigate = useNavigate();

  const pageData = {
    title: 'ISO 21001:2018 Educational Organizations Training',
    subtitle: 'Implement World-Class Educational Organization Management Systems (EOMS)',
    description: 'Comprehensive training on ISO 21001:2018 implementation, learner satisfaction, competency-based education, EOMS requirements, and quality management for educational institutions',
    meta_title: 'ISO 21001 Training 2024 | Educational Organization Management System | EOMS | ISDE Academy',
    meta_description: 'Professional ISO 21001:2018 EOMS training. Achieve 28% learner satisfaction increase, 33% staff engagement improvement. Master requirements, implementation, and certification for educational organizations.',
    meta_keywords: 'ISO 21001 training, EOMS, educational organization management system, learner satisfaction, ISO 21001:2018, competency-based education, quality management education',
    canonicalUrl: 'https://isde.academy/training/iso-21001-educational-organizations'
  };

  const trainingModules = [
    {
      title: 'ISO 21001:2018 Framework & Requirements',
      icon: Award,
      duration: '5 Hours',
      topics: [
        'Understanding ISO 21001:2018 Structure and Philosophy',
        'High-Level Structure (HLS) Alignment with ISO Standards',
        'Context of the Educational Organization (Clause 4)',
        'Leadership and Commitment (Clause 5)',
        'Risk and Opportunity Management',
        'Stakeholder Needs and Expectations',
        'Scope Definition for Educational Organizations',
        'Differences from ISO 9001 Quality Management'
      ],
      outcomes: [
        'Complete understanding of ISO 21001:2018 requirements',
        'Clarity on EOMS framework and structure',
        'Knowledge of stakeholder-focused approach'
      ]
    },
    {
      title: 'Learner Satisfaction & Engagement',
      icon: Heart,
      duration: '4 Hours',
      topics: [
        'Learner Satisfaction Evaluation Methods',
        'Performance Monitoring and Measurement (Clause 9)',
        'Learner Achievement and Competency Development',
        'Learning Environment Quality Assessment',
        'Feedback Collection and Analysis',
        'Learner Engagement Strategies',
        'Best Practices for 28% Satisfaction Increase',
        'Continuous Improvement Based on Feedback'
      ],
      outcomes: [
        'Expertise in measuring learner satisfaction',
        'Proven strategies for engagement enhancement',
        'Data-driven improvement methodologies'
      ]
    },
    {
      title: 'Competency-Based Education Delivery',
      icon: GraduationCap,
      duration: '5 Hours',
      topics: [
        'Competence Acquisition Through Teaching and Learning',
        'Program Design and Curriculum Development',
        'Learning Objectives and Outcome Specification',
        'Assessment Methods for Competency Verification',
        'Personalized Learning Approaches',
        'Technology Integration in Education',
        'Faculty Competence and Development',
        'Research and Innovation Support'
      ],
      outcomes: [
        'Mastery of competency-based education principles',
        'Ability to design outcome-focused programs',
        'Effective assessment and verification methods'
      ]
    },
    {
      title: 'EOMS Planning & Implementation',
      icon: Target,
      duration: '6 Hours',
      topics: [
        'Planning for EOMS (Clause 6)',
        'Objectives and Planning to Achieve Them',
        'Resource Management (Clause 7)',
        'Infrastructure and Learning Environment',
        'Control of Externally Provided Processes',
        'Documented Information Requirements',
        'Operational Planning and Control (Clause 8)',
        'Program Design, Development, and Delivery',
        'Emergency Preparedness and Response'
      ],
      outcomes: [
        'Complete EOMS implementation roadmap',
        'Resource planning and allocation skills',
        'Operational control expertise'
      ]
    },
    {
      title: 'Performance Evaluation & Internal Auditing',
      icon: BarChart3,
      duration: '5 Hours',
      topics: [
        'Monitoring, Measurement, Analysis, and Evaluation',
        'Learner Achievement Metrics',
        'Faculty and Staff Satisfaction Tracking',
        'Program Effectiveness and Outcomes',
        'Internal Audit Planning and Execution',
        'Management Review Process (Clause 9.3)',
        'Data Analysis and Reporting',
        'KPI Development for Educational Organizations'
      ],
      outcomes: [
        'Comprehensive performance evaluation skills',
        'Internal audit competence for EOMS',
        'Strategic management review capabilities'
      ]
    },
    {
      title: 'Continuous Improvement & Certification',
      icon: TrendingUp,
      duration: '4 Hours',
      topics: [
        'Improvement Strategies (Clause 10)',
        'Nonconformity and Corrective Action',
        'Preventive Action Implementation',
        'Innovation and Best Practice Integration',
        'Preparing for ISO 21001 Certification Audit',
        'Certification Body Selection and Process',
        'Government Recognition and Accreditation Benefits',
        'Post-Certification Maintenance and Surveillance'
      ],
      outcomes: [
        'Systematic continuous improvement framework',
        'Certification readiness and audit preparation',
        'Long-term EOMS sustainability'
      ]
    }
  ];

  const whyChoose = [
    {
      title: 'UNESCO Validated',
      description: '28% learner satisfaction and 33% staff engagement increase reported',
      icon: Globe,
      color: 'emerald'
    },
    {
      title: 'Global Recognition',
      description: 'Government recognition and accreditation benefits worldwide',
      icon: Award,
      color: 'blue'
    },
    {
      title: 'Proven Methodology',
      description: 'Systematic approach to educational quality management',
      icon: Lightbulb,
      color: 'purple'
    },
    {
      title: 'Expert Guidance',
      description: 'Trainers with ISO 21001 certification and implementation experience',
      icon: Star,
      color: 'orange'
    }
  ];

  const keyBenefits = [
    {
      title: 'For Educational Institutions',
      icon: Building2,
      benefits: [
        'Enhanced learner satisfaction (28% average increase)',
        'Improved staff engagement (33% improvement)',
        'Global recognition and credibility',
        'Government funding eligibility and accreditation',
        'Transparent and accountable operations',
        'Competitive advantage in education sector',
        'Systematic quality improvement processes'
      ]
    },
    {
      title: 'For Quality Managers & Leaders',
      icon: GraduationCap,
      benefits: [
        'Complete mastery of ISO 21001:2018 requirements',
        'Expertise in EOMS implementation',
        'Learner-centric approach to quality management',
        'Internal audit and management review skills',
        'Certification preparation competence',
        'Career advancement in educational quality',
        'International recognition as EOMS professional'
      ]
    }
  ];

  const deliveryOptions = [
    {
      mode: 'On-Campus Workshop',
      duration: '3-4 Days Intensive',
      description: 'Comprehensive on-site training with gap analysis, EOMS documentation, process mapping, and internal audit simulation for institutional teams',
      ideal: 'Best for institutions planning ISO 21001 certification in next 6-12 months'
    },
    {
      mode: 'Online Live Training',
      duration: '8-10 Sessions (3 weeks)',
      description: 'Interactive online sessions with recorded access, EOMS templates, audit checklists, and post-training implementation support',
      ideal: 'Flexible option for quality teams and leadership across campuses'
    },
    {
      mode: 'Hybrid Consulting',
      duration: 'Customized (3-8 months)',
      description: 'End-to-end ISO 21001 implementation support including gap analysis, documentation, process development, internal audits, and certification audit support',
      ideal: 'Complete solution for first-time EOMS implementation and certification'
    }
  ];

  const targetAudience = [
    'Quality Assurance Directors and Managers',
    'IQAC Coordinators and Members',
    'Vice-Chancellors and Principals',
    'Academic Deans and Department Heads',
    'Accreditation Coordinators',
    'ISO Management Representatives',
    'Educational Administrators and Staff'
  ];

  return (
    <>
      <Helmet>
        <title>{pageData.meta_title}</title>
        <meta name="description" content={pageData.meta_description} />
        <meta name="keywords" content={pageData.meta_keywords} />
        <link rel="canonical" href={pageData.canonicalUrl} />
        <meta property="og:title" content={pageData.meta_title} />
        <meta property="og:description" content={pageData.meta_description} />
        <meta property="og:url" content={pageData.canonicalUrl} />
        <meta name="twitter:title" content={pageData.meta_title} />
        <meta name="twitter:description" content={pageData.meta_description} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-teal-600 via-cyan-600 to-blue-700 text-white py-24 mt-20">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 -left-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob"></div>
            <div className="absolute top-0 -right-4 w-72 h-72 bg-white rounded-full mix-blend-overlay filter blur-xl animate-blob animation-delay-2000"></div>
          </div>

          <div className="absolute top-10 right-10 opacity-20">
            <Award className="h-32 w-32" />
          </div>

          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center max-w-4xl mx-auto"
            >
              <motion.div
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                <Globe className="h-5 w-5 text-yellow-200" />
                <span className="text-sm font-semibold">UNESCO Validated Results</span>
              </motion.div>

              <h1 className="text-4xl md:text-6xl font-bold mb-6">{pageData.title}</h1>
              <p className="text-xl md:text-2xl text-teal-100 mb-4">{pageData.subtitle}</p>
              <p className="text-lg text-teal-50 mb-8 max-w-3xl mx-auto">{pageData.description}</p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/contact')}
                  className="bg-white text-teal-600 hover:bg-teal-50 px-8 py-6 text-lg font-semibold"
                >
                  Request Training
                </Button>
                <Button
                  onClick={() => navigate('/training-calendar')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-teal-600 px-8 py-6 text-lg font-semibold"
                >
                  View Schedule
                </Button>
              </div>
            </motion.div>
          </div>

          <div className="absolute bottom-0 left-0 right-0">
            <svg viewBox="0 0 1440 120" fill="none">
              <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#F9FAFB"/>
            </svg>
          </div>
        </section>

        {/* Training Modules */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">Comprehensive Training Modules</h2>
          <p className="text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Structured curriculum covering all clauses of ISO 21001:2018 from requirements to certification
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {trainingModules.map((module, index) => {
              const IconComponent = module.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-lg transition-all">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4 mb-4">
                        <div className="bg-teal-50 p-3 rounded-lg">
                          <IconComponent className="h-6 w-6 text-teal-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold mb-1">{module.title}</h3>
                          <p className="text-sm text-teal-600 font-semibold">Duration: {module.duration}</p>
                        </div>
                      </div>

                      <div className="mb-4">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Key Topics:</h4>
                        <ul className="space-y-1.5">
                          {module.topics.map((topic, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                              <CheckCircle2 className="h-4 w-4 text-teal-500 flex-shrink-0 mt-0.5" />
                              <span>{topic}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="bg-teal-50 p-3 rounded-lg">
                        <h4 className="text-xs font-semibold text-teal-700 uppercase mb-1">Learning Outcomes:</h4>
                        <ul className="space-y-1">
                          {module.outcomes.map((outcome, i) => (
                            <li key={i} className="text-sm text-gray-700 flex items-start gap-1.5">
                              <span className="text-teal-600">•</span>
                              {outcome}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Why Choose Section */}
        <section className="bg-white py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Why Choose Our ISO 21001 Training?</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whyChoose.map((item, index) => {
                const IconComponent = item.icon;
                const colors = {
                  emerald: 'bg-emerald-50 text-emerald-600',
                  blue: 'bg-blue-50 text-blue-600',
                  purple: 'bg-purple-50 text-purple-600',
                  orange: 'bg-orange-50 text-orange-600'
                };

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="h-full text-center hover:shadow-lg transition-all">
                      <CardContent className="p-6">
                        <div className={`${colors[item.color]} w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4`}>
                          <IconComponent className="h-6 w-6" />
                        </div>
                        <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                        <p className="text-sm text-gray-600">{item.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Key Benefits */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">Key Benefits</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {keyBenefits.map((item, index) => {
              const IconComponent = item.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                >
                  <Card className="h-full">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-teal-50 p-3 rounded-lg">
                          <IconComponent className="h-6 w-6 text-teal-600" />
                        </div>
                        <h3 className="text-xl font-bold">{item.title}</h3>
                      </div>
                      <ul className="space-y-2.5">
                        {item.benefits.map((benefit, i) => (
                          <li key={i} className="flex items-start gap-2.5">
                            <ChevronRight className="h-5 w-5 text-teal-500 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Delivery Options */}
        <section className="bg-gradient-to-br from-gray-50 to-teal-50 py-16">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">Training Delivery Options</h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              Choose the format that best suits your institution's needs and timeline
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              {deliveryOptions.map((option, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full hover:shadow-xl transition-all hover:-translate-y-1">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold mb-2 text-teal-600">{option.mode}</h3>
                      <p className="text-sm font-semibold text-gray-700 mb-3">{option.duration}</p>
                      <p className="text-gray-600 mb-4">{option.description}</p>
                      <div className="bg-teal-50 p-3 rounded-lg">
                        <p className="text-xs font-semibold text-teal-700 uppercase mb-1">Ideal For:</p>
                        <p className="text-sm text-gray-700">{option.ideal}</p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Target Audience */}
        <section className="max-w-7xl mx-auto px-4 py-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center">Who Should Attend?</h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            This training is essential for all stakeholders involved in quality management and ISO 21001 implementation
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {targetAudience.map((audience, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-3 bg-white p-4 rounded-lg shadow-sm border border-teal-100 hover:shadow-md transition-all"
              >
                <Users className="h-5 w-5 text-teal-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{audience}</span>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-600 text-white py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Ready to Achieve ISO 21001 Certification?
              </h2>
              <p className="text-xl text-teal-50 mb-8">
                Join institutions achieving 28% learner satisfaction increase and 33% staff engagement improvement with ISO 21001 EOMS
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/contact')}
                  className="bg-white text-teal-600 hover:bg-teal-50 px-8 py-6 text-lg font-semibold"
                >
                  Schedule Training
                </Button>
                <Button
                  onClick={() => navigate('/training/hei-training')}
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-teal-600 px-8 py-6 text-lg font-semibold"
                >
                  View All HEI Programs
                </Button>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ISO21001EducationalOrganizations;
